import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Database, Trophy, ShieldCheck } from 'lucide-react'
import { HeroBanner } from './ui/HeroBanner'
import { AvailableModules } from './ui/AvailableModules'
import { useUser } from '../context/UserContext'
import { usePageHeader } from '../context/PageHeaderContext'
import { coursesAPI } from '../api/courses'
import { formatDuration } from '../utils/duration'
import type { LabModule } from '../types/api'
import { useBreadcrumbs } from '@renderer/context/BreadcrumbContext'

interface LabModuleWithProgress extends LabModule {
  status?: string
  completed_exercises?: number
  total_exercises?: number
}

interface ModulesProgressCardProps {
  completed: number
  total: number
  progressPct: number
}

const ModulesProgressCard = ({ completed, total, progressPct }: ModulesProgressCardProps): React.JSX.Element => (
  <div
    className="flex flex-col gap-3 px-8 py-6"
    style={{
      background: '#f5ede0',
      borderRadius: '16px',
      border: '1px solid #e0d4be',
      minWidth: '220px'
    }}
  >
    <Trophy className="w-6 h-6 text-[#7a6020]" />
    <div>
      <p className="text-sm text-gray-500 font-medium">Overall Progress</p>
      <div className="flex items-baseline gap-2">
        <p className="text-3xl font-bold text-gray-900">{progressPct}%</p>
      </div>
    </div>
    <div style={{ width: '100%', height: '6px', background: '#e0d4be', borderRadius: '3px' }}>
      <div style={{ width: `${progressPct}%`, height: '100%', background: '#7a6020', borderRadius: '3px' }} />
    </div>
    <p className="text-xs text-gray-500">{completed} of {total} modules completed</p>
  </div>
)

export const LabModulesPage = (): React.JSX.Element => {
  const { courseId } = useParams<{ courseId: string }>()
  const { user } = useUser()
  const { setSubtitle } = usePageHeader()
  const { setBreadcrumbs } = useBreadcrumbs()
  
  const [modules, setModules] = useState<LabModuleWithProgress[]>([])
  const [loading, setLoading] = useState(true)
  const [courseName, setCourseName] = useState<string>('')
  const [courseDescription, setCourseDescription] = useState<string>('')
  const [courseLevel, setCourseLevel] = useState<string>('')
  const [totalDurationMinutes, setTotalDurationMinutes] = useState<number>(0)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!courseId) return
      try {
        const courseData = await coursesAPI.getCourse(courseId)
        const name = courseData.title || 'Course'
        setCourseName(name)
        setBreadcrumbs([
          { label: 'Courses', path: '/courses' },
          { label: name, path: `/courses/${courseId}` }
        ])
        setCourseDescription(courseData.description || '')
        setCourseLevel(courseData.level || 'Technical Track')
      } catch {
        // ignore — AvailableModules handles its own error state
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [courseId, user?.id, setSubtitle])

  const completedModules = modules.filter((m) => (m.progress ?? 0) >= 100).length
  const totalModules = modules.length
  const overallProgress =
    totalModules > 0
      ? Math.round(modules.reduce((sum, m) => sum + (m.progress ?? 0), 0) / totalModules)
      : 0

  return (
    <div>
      <div className="max-w-[1200px] mx-auto space-y-6">
        <HeroBanner
          tag={
            <>
              <Database className="w-3.5 h-3.5" />
              {courseLevel}
            </>
          }
          title={courseName}
          description={
            courseDescription ||
            (totalDurationMinutes > 0
              ? `${formatDuration(totalDurationMinutes)} of hands-on lab exercises across all modules.`
              : 'Explore the lab modules for this course and track your progress through hands-on exercises.')
          }
          rightContent={
            !loading && totalModules > 0 ? (
              <ModulesProgressCard
                completed={completedModules}
                total={totalModules}
                progressPct={overallProgress}
              />
            ) : undefined
          }
        />

        <AvailableModules
          courseId={courseId}
          onModulesLoaded={setModules}
          onTotalDurationMinutes={setTotalDurationMinutes}
        />

        {!loading && totalModules > 0 && (
          <div
            className="flex items-center justify-between px-6 py-5 rounded-2xl"
            style={{ background: '#ffffff', border: '1px solid #e0d4be' }}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: '#f5ede0' }}
              >
                <ShieldCheck className="w-6 h-6" style={{ color: '#7a6020' }} />
              </div>
              <div>
                <p className="text-base font-bold text-gray-900">Ready for Assessment?</p>
                <p className="text-sm text-gray-500">
                  Complete all modules to unlock the final ONTAP Administration Practice Exam and earn your certification badge.
                </p>
              </div>
            </div>
            <button
              disabled={completedModules < totalModules}
              className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
              style={{
                background: completedModules >= totalModules ? '#7a6020' : '#E5E7EB',
                color: completedModules >= totalModules ? '#fff' : '#9CA3AF',
                cursor: completedModules >= totalModules ? 'pointer' : 'default'
              }}
            >
              Unlock Certification Exam
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
